/**
 * Names that mark a modern, neural-sounding voice. Chrome's "Google" set and
 * Edge's "Natural"/"Neural" set are the good ones; Apple's better voices are
 * labelled Premium/Enhanced, or are the Siri cuts.
 */
const GOOD_VOICE_MARKERS = [
    'natural',
    'neural',
    'google',
    'siri',
    'premium',
    'enhanced',
];

/**
 * Apple's novelty and legacy compact voices. They are installed by default and
 * sort early, so without this the bar picks one of them and sounds like 1998.
 */
const NOVELTY_VOICES = [
    'albert',
    'bad news',
    'bahh',
    'bells',
    'boing',
    'bubbles',
    'cellos',
    'deranged',
    'good news',
    'jester',
    'organ',
    'superstar',
    'trinoids',
    'whisper',
    'wobble',
    'zarvox',
    'fred',
    'junior',
    'ralph',
    'kathy',
    'princess',
];

/** Locale preference, nearest audience first. */
const LOCALE_RANK = ['en-nz', 'en-au', 'en-gb', 'en-in', 'en-us', 'en'];

const localeScore = (lang: string) => {
    const normalised = lang.toLowerCase().replace('_', '-');
    const index = LOCALE_RANK.findIndex((code) => normalised.startsWith(code));
    return index === -1 ? -1 : LOCALE_RANK.length - index;
};

/**
 * Scores one voice. Higher is better; a negative score means "never pick this".
 *
 * The ranking is about how the voice sounds first and where it is from second:
 * a neural US voice reads an article far better than a robotic New Zealand one.
 */
const scoreVoice = (voice: SpeechSynthesisVoice) => {
    const name = voice.name.toLowerCase();
    if (NOVELTY_VOICES.some((novelty) => name.includes(novelty))) return -1;

    const locale = localeScore(voice.lang);
    if (locale < 0) return -1;

    let score = locale;

    if (GOOD_VOICE_MARKERS.some((marker) => name.includes(marker))) {
        score += 100;
    }

    // Chrome's network voices are its good ones, and are the difference between
    // "a computer reading" and "someone reading". Weighted above locale, below
    // an explicitly neural name.
    if (!voice.localService) score += 50;

    return score;
};

/**
 * The best voice the device has for reading an article aloud.
 *
 * `speechSynthesis` hands back whatever the platform's default is unless asked
 * otherwise, and that default is routinely the oldest, most robotic voice
 * installed — so choosing is the single biggest thing that makes the reading
 * sound human. Returns null when nothing usable is installed, which leaves the
 * platform default in place.
 */
export const pickSpeechVoice = (voices: SpeechSynthesisVoice[]) => {
    let best: SpeechSynthesisVoice | null = null;
    let bestScore = 0;

    for (const voice of voices) {
        const score = scoreVoice(voice);
        if (score > bestScore) {
            best = voice;
            bestScore = score;
        }
    }

    return best;
};
